import {
  Badge,
  Box,
  Center,
  HStack,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { DataTable, DataTablePageSize } from '@gamma/ui/data-table';
import { LoadingSpinner } from '@gamma/ui/progress';
import moment from 'moment';
import { useContext, useEffect, useMemo } from 'react';
import { Column, SortingRule, Column as TableColumn } from 'react-table';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  SlideDrawerContext,
} from '@investigator/shared/providers';
import { SeverityScore } from '@investigator/shared/components';
import { Detection } from '@investigator/shared/generated/graphql';
import {
  getDetectionSummary,
  stringifyAlertType,
} from '@investigator/shared/utils';

import { AlertEntityPopover, SantizedDetection } from '../../pages';
import { DetectionDrawer, DetectionDrawerActions } from '../DetectionDrawer';
import { SendToPill } from '../DetectionPills';
import { DetectionStatusPill } from '../DetectionStatusPill';
import { useDetectionContext } from '../../context';

const { controls, detection } = i18n.pages.detections;
const { unassigned, externalTenant } = detection;

interface DetectionsTableViewProps {
  offset: number;
  pageSize: number;
  pageIndex: number;
  pageCount: number;
  sort_by: string | null;
  isRelated?: boolean;
  params: URLSearchParams;
  detectionsLoading: boolean;
  formatSort: (value: string) => { id: string; desc: boolean }[];
  handleFetchData: (
    pageIndex: number,
    pageSize: number,
    sortBy?: SortingRule<SantizedDetection>[],
  ) => void;
  getSortsForAPI: (
    sortBy: SortingRule<Detection>[],
  ) => { sort_by: string; sort_dir: string }[];
  dateRangeChangeKey: string; // Accept dateRangeChangeKey as a prop
  hideNotesColumn?: boolean;
  hideDetectionMethodColumn?: boolean;
  hideAlertTypeColumn?: boolean;
  hideAlertCountColumn?: boolean;
  categoryColumnHeader?: string;
  enableBulkSelection?: boolean;
  disableDrawer?: boolean;
}

export const DetectionsTableView = ({
  params,
  isRelated,
  offset,
  sort_by,
  pageSize,
  pageIndex,
  pageCount,
  formatSort,
  getSortsForAPI,
  handleFetchData,
  detectionsLoading,
  dateRangeChangeKey,
  hideNotesColumn = true,
  hideDetectionMethodColumn = true,
  hideAlertTypeColumn = false,
  hideAlertCountColumn = false,
  categoryColumnHeader,
  enableBulkSelection = false,
  disableDrawer = false,
}: DetectionsTableViewProps) => {
  const { isOrgTenant } = useContext(AuthContext);

  const { start, end, sevenDayStart, sevenDayEnd } = useDateRangeQueryString();

  // Use URL params if valid, otherwise fallback to stable 7-day values from hook
  const validStart = start && !isNaN(start) ? start : sevenDayStart;
  const validEnd = end && !isNaN(end) ? end : sevenDayEnd;

  const { setSelectedItem, queryVariables, detectionsData } =
    useDetectionContext();

  const {
    setIsSlideDrawerOpen,
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setSlideDrawerActions,
  } = useContext(SlideDrawerContext);

  const hiddenCols = useMemo(
    () => [
      'detection_id',
      ...(!isOrgTenant ? ['tenant_info.tenant_display_name'] : []),
      ...(hideNotesColumn ? ['notes_count'] : []),
      ...(hideDetectionMethodColumn ? ['detection_method'] : []),
      ...(hideAlertTypeColumn ? ['alert_info.alert_type'] : []),
      ...(hideAlertCountColumn ? ['total_alert_count'] : []),
    ],
    [
      isOrgTenant,
      hideNotesColumn,
      hideDetectionMethodColumn,
      hideAlertTypeColumn,
      hideAlertCountColumn,
    ],
  );

  useEffect(() => {
    if (params.get('view') !== 'table') {
      params.set('view', 'table');
    }
  }, [params]);

  const dataTableColumns: TableColumn<SantizedDetection>[] = useMemo(
    () => [
      {
        width: 100,
        Header: i18n.tables.score,
        accessor: 'rank.severity',
        Cell: (props) => {
          return <SeverityScore score={props.value ?? 0} />;
        },
      },
      {
        Header: categoryColumnHeader || i18n.tables.detection,
        accessor: 'alert_info.alert_name',
        disableSortBy: true,
      },
      {
        width: 175,
        Header: 'Source',
        accessor: 'paginated_source_entities',
        disableSortBy: true,
        Cell: (props) => {
          const detectionSummary = getDetectionSummary(
            props.row.original as Detection,
            props?.value?.alert_entities?.[0],
          );
          return (
            <AlertEntityPopover
              entities={props?.value?.alert_entities}
              detectionSummary={detectionSummary}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        width: 175,
        Header: 'Destination',
        accessor: 'paginated_destination_entities',
        disableSortBy: true,
        Cell: (props) => {
          const detectionSummary = getDetectionSummary(
            props.row.original as Detection,
            props?.value?.alert_entities?.[0],
          );
          return (
            <AlertEntityPopover
              entities={props?.value?.alert_entities}
              detectionSummary={detectionSummary}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        width: 150,
        Header: i18n.tables.detectionMethod,
        accessor: 'detection_method',
        id: 'detection_method',
        disableSortBy: true,
        Cell: ({ row: { original } }) => (
          <Text fontSize="sm">{original.detection_method || 'Signature'}</Text>
        ),
      },
      {
        width: 100,
        Header: i18n.tables.notes,
        accessor: 'notes_count',
        disableSortBy: true,
        Cell: ({ value = 0 }) =>
          value === 0 ? (
            <Text color="text.secondary">0</Text>
          ) : (
            <HStack spacing={1}>
              <Text>{value}</Text>
            </HStack>
          ),
      },
      {
        width: 150,
        Header: i18n.tables.tenant,
        accessor: 'tenant_info.tenant_display_name',
        disableSortBy: true,
        Cell: (props) => (
          <Badge isTruncated variant="outline" maxW="100%">
            {props.value}
          </Badge>
        ),
      },
      {
        width: 150,
        Header: i18n.tables.alertType,
        accessor: 'alert_info.alert_type',
        disableSortBy: true,
        Cell: (props) => {
          return <>{stringifyAlertType(props.value)}</>;
        },
      },
      {
        width: 100,
        Header: i18n.tables.numAlerts,
        accessor: 'total_alert_count',
        disableSortBy: true,
      },
      {
        width: 150,
        Header: i18n.tables.status,
        accessor: 'detection_status',
        disableSortBy: true,
        Cell: (props) => {
          return (
            <HStack>
              <DetectionStatusPill
                text={props.value}
                isStatusOpen={props.value === 'open'}
              />
              {props?.row?.original?.escalation_data?.escalation_status && (
                <SendToPill
                  escalation_data={props?.row?.original?.escalation_data}
                />
              )}
            </HStack>
          );
        },
      },
      {
        width: 150,
        Header: i18n.tables.assignee,
        accessor: 'assignment_info.assigned_to_user_alias',
        disableSortBy: true,
        Cell: (props) => {
          return (
            <Tag
              size="md"
              variant="solid"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              data-testid="detection-alert-assignee"
              colorScheme={props?.value ? 'purple' : 'gray'}
            >
              <Tooltip
                label={
                  !props?.value
                    ? unassigned
                    : props?.value === 'External User'
                      ? externalTenant
                      : props?.value
                }
              >
                <Text isTruncated>
                  {!props?.value ? unassigned : props?.value}
                </Text>
              </Tooltip>
            </Tag>
          );
        },
      },
      {
        Header: i18n.tables.lastAlertTime,
        accessor: 'latest_start_timestamp',
        Cell: (props) => {
          return <>{moment.unix(props.value || 0).format('MMMM Do, h:mma')}</>;
        },
      },
    ],
    [categoryColumnHeader, validStart, validEnd],
  );

  const filteredDataTableColumns = useMemo(
    () => [
      ...dataTableColumns.filter(
        (col) => !hiddenCols.includes(col.accessor as string) && col,
      ),
      {
        Header: '',
        accessor: 'detection-actions-menu-expanded',
        Cell: () => null,
      },
    ],
    [hiddenCols, dataTableColumns],
  );

  const sanitizedDetectionsData =
    detectionsData?.queryDetectionsPaginated?.detections?.map(
      ({
        rank,
        tenant_info,
        tenant,
        alert_info,
        update_info,
        alert_entity,
        detection_id,
        detection_keys,
        assignment_info,
        escalation_data,
        detection_status,
        total_alert_count,
        latest_start_timestamp,
        earliest_start_timestamp,
        paginated_source_entities,
        paginated_destination_entities,
        comment_count,
      }: Detection) => ({
        rank,
        tenant_info,
        tenant,
        alert_info,
        update_info,
        alert_entity,
        detection_id,
        detection_keys,
        assignment_info,
        escalation_data,
        detection_status,
        total_alert_count,
        latest_start_timestamp,
        earliest_start_timestamp,
        paginated_source_entities,
        paginated_destination_entities,
        notes_count: comment_count || 0,
      }),
    ) || [];

  if (pageIndex > 0 && sanitizedDetectionsData.length === 0)
    return <LoadingSpinner />;

  return (
    <Box
      borderTop="1px solid"
      borderColor="border.layer.2"
      data-testid="data-table-wrapper"
    >
      <DataTable
        key={dateRangeChangeKey}
        getRowId={(row) => row.detection_id}
        isSelectable={enableBulkSelection}
        isExpandable
        isMemod={true}
        pageSize={pageSize as DataTablePageSize | undefined}
        pageCount={pageCount}
        autoResetSortBy={false}
        isPaginationManual={true}
        onFetchData={handleFetchData}
        onRowClick={
          disableDrawer
            ? undefined
            : ({ original }, event) => {
                if (
                  !detectionsLoading &&
                  (event.target as HTMLElement)?.className?.includes &&
                  !['icon', 'link', 'menu', 'button', 'switch'].some(
                    (classNameString) =>
                      (event.target as HTMLElement).className.includes(
                        classNameString,
                      ),
                  )
                ) {
                  setIsSlideDrawerOpen?.(false);
                  setPortalType?.('detection');
                  setSelectedItem(original as SantizedDetection);
                  if (original?.detection_id) {
                    params.set('detection_id', original.detection_id);
                  }
                  setSlideDrawerTitle?.(
                    <Box
                      key={`${original?.detection_id}-detection-portal-title`}
                      __css={{ wordWrap: 'anywhere' }}
                    >
                      {`${original?.alert_info?.alert_name} | ${original?.alert_entity?.entity_name}`}
                    </Box>,
                  );
                  setSlideDrawerBody?.(
                    <DetectionDrawer
                      key={`${original?.detection_id}-detection-portal-body`}
                      item={original}
                      disableBorders={true}
                      queryDetectionsPaginatedVariables={{
                        query: queryVariables?.query ?? '',
                        size: queryVariables?.size ?? 0,
                        offset: queryVariables?.offset ?? 0,
                        sort: getSortsForAPI(
                          formatSort(queryVariables?.sortBy ?? ''),
                        ),
                      }}
                    />,
                  );
                  setSlideDrawerActions?.(
                    <Box w="100%" my={2}>
                      <DetectionDrawerActions
                        key={`${original?.detection_id}-detection-portal-actions`}
                        detection={original as unknown as Detection}
                        queryDetectionsPaginatedVariables={{
                          query: queryVariables?.query ?? '',
                          size: queryVariables?.size ?? 0,
                          offset: queryVariables?.offset ?? 0,
                          sort: getSortsForAPI(
                            formatSort(queryVariables?.sortBy ?? ''),
                          ),
                        }}
                      />
                    </Box>,
                  );
                  setIsSlideDrawerOpen?.(true);
                }
              }
        }
        columns={filteredDataTableColumns as Column<SantizedDetection>[]}
        emptyBodyContent={
          detectionsLoading ? (
            <LoadingSpinner />
          ) : detectionsData?.queryDetectionsPaginated?.total_items === 0 ? (
            <Center>{controls.noResults}</Center>
          ) : null
        }
        autoResetPage={offset / pageSize >= pageCount}
        data={
          !detectionsLoading
            ? (sanitizedDetectionsData as SantizedDetection[])
            : []
        }
        itemCount={detectionsData?.queryDetectionsPaginated?.total_items}
        initialState={{
          pageIndex,
          sortBy: sort_by
            ? formatSort(sort_by)
            : formatSort(queryVariables?.sortBy ?? ''),
        }}
        RowActions={(detectionData) =>
          queryVariables &&
          !detectionsLoading && (
            <DetectionDrawerActions
              isRowActions
              isRelated={isRelated}
              detection={detectionData as unknown as Detection}
              queryDetectionsPaginatedVariables={{
                query: queryVariables.query,
                size: queryVariables.size,
                offset: queryVariables.offset,
                sort: getSortsForAPI(formatSort(queryVariables.sortBy ?? '')),
              }}
            />
          )
        }
      />
    </Box>
  );
};
